import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useCallback, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import RowLoader from "../UI/RowLoader";

const FormField = ({
  label,
  name,
  placeholder,
  required,
  type = "textarea",
  isError = false,
}) => (
  <div className="tw-mb-4 tw-flex tw-flex-col sm:tw-flex-row">
    <label
      htmlFor={name}
      className="tw-w-[180px] tw-text-[#0B2787] tw-font-medium"
    >
      {label}: {required && <span className="tw-text-red-600">*</span>}
    </label>
    <div className="tw-w-full">
      <Field
        as={type}
        id={name}
        name={name}
        className={`form-control tw-min-h-[100px] !tw-bg-[#eee] tw-p-2 tw-border tw-rounded-md focus:tw-outline-none focus:tw-border-blue-500 ${
          isError ? "is-invalid" : ""
        }`}
        placeholder={placeholder}
      />
      <ErrorMessage
        name={name}
        component="div"
        className="tw-text-red-600 tw-text-sm tw-mt-1"
      />
    </div>
  </div>
);

const AccountDetailsAction = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState("");
  const { clientId } = useParams();

  const handleSubmit = useCallback(async (values, { resetForm }) => {
    setIsLoading(true);
    try {
      await axios.post(
        `/admin/reviewClientAccount/reviewAccount/${clientId}`,
        values
      );
      let redirectToTab;
      switch (values?.actionType) {
        case "reject":
          redirectToTab = "needsModification";
          break;
        case "accept":
          redirectToTab = "confirmed";
          break;
        default:
          redirectToTab = "pending";
      }
      Swal.fire({
        title: "Success",
        text: "Verification has been successfully completed for this account.",
        icon: "success",
      });
      navigate(`/accounts-review?tab=${redirectToTab}`);
      resetForm();
    } catch (error) {
      Swal.fire({
        title: "Error",
        text:
          error.response.data.message ||
          "Error where add action on client account",
        icon: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        actionType: Yup.string().required("Verification status is required"),
        notesForPartner: Yup.string().when("actionType", {
          is: (val) => val === "reject",
          then: (schema) =>
            schema
              .required(
                "This field is required when Verification status is needs modification"
              )
              .min(5, "Must be at least 5 characters"),
          otherwise: (schema) => schema.notRequired(),
        }),
      }),
    []
  );

  return (
    <section className="tw-container tw-mx-auto tw-p-6 tw-bg-white tw-rounded-lg tw-shadow-md">
      <h3 className="tw-text-xl tw-font-semibold tw-text-red-600 tw-mb-4 tw-border-b tw-pb-2">
        Action
      </h3>
      <Formik
        initialValues={{
          notesForPartner: "",
          notesForAdmin: "",
          actionType: "",
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, values, touched, errors, isValid }) => (
          <Form className="tw-overflow-x-auto">
            <div className="tw-flex tw-flex-col sm:tw-flex-row tw-mb-4">
              <label className="tw-w-[150px] tw-text-[#0B2787] tw-font-medium">
                Verification: 
              </label>
              <div className="tw-flex tw-gap-2 md:tw-gap-10 tw-flex-col md:tw-flex-row">
                <div className="form-check tw-flex tw-items-center tw-gap-2">
                  <Field
                    type="radio"
                    name="actionType"
                    value="accept"
                    className="form-check-input"
                    id="accept"
                  />
                  <label htmlFor="accept">Verified</label>
                </div>
                <div className="form-check tw-flex tw-items-center tw-gap-2">
                  <Field
                    type="radio"
                    name="actionType"
                    value="reject"
                    className="form-check-input"
                    id="reject"
                  />
                  <label htmlFor="reject">Need Modification</label>
                </div>
              </div>
            </div>

            <FormField
              label="Notes for partner"
              name="notesForPartner"
              placeholder="That appears for partner"
              required={values.actionType === "reject"}
              isError={touched.notesForPartner && errors.notesForPartner}
            />
            <FormField
              label="Notes for admin"
              name="notesForAdmin"
              placeholder="That appears for admin"
            />

            <div className="tw-text-right">
              <button
                type="submit"
                className="tw-bg-[#1B2E71] !tw-rounded-md tw-min-w-36 tw-text-white tw-py-2 tw-font-medium hover:tw-bg-blue-700 disabled:tw-opacity-50"
                disabled={isSubmitting || !isValid}
              >
                {isLoading ? (
                  <div className="tw-flex tw-justify-center">
                    <RowLoader paddingStyle="tw-py-0" />
                  </div>
                ) : (
                  "Submit"
                )}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </section>
  );
};

export default AccountDetailsAction;
